import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { IVideoClip, VideoEditConfig, IEpisode } from '@packages/global';
import { FFmpegCDNLoader } from './ffmpegCDNLoader';
import { SRTGenerator } from './srtGenerator';
import JSZip from 'jszip';

export interface StoryboardExportProgress {
  stage: 'loading' | 'downloading' | 'processing' | 'packaging' | 'complete';
  progress: number;
  message: string;
  currentClip?: number;
  totalClips?: number;
}

export class StoryboardExporter {
  private ffmpeg: FFmpeg | null = null;
  private isLoaded = false;
  private onProgress?: (progress: StoryboardExportProgress) => void;

  constructor(onProgress?: (progress: StoryboardExportProgress) => void) {
    this.onProgress = onProgress;
  }

  async load(): Promise<void> {
    if (this.isLoaded) return;

    const loader = new FFmpegCDNLoader((loadProgress) => {
      this.updateProgress({
        stage: 'loading',
        progress: loadProgress.progress,
        message: loadProgress.message
      });
    });

    const { ffmpeg } = await loader.loadFFmpeg();
    this.ffmpeg = ffmpeg;
    this.isLoaded = true;
  }

  async exportStoryboardMaterials(
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig,
    episode: IEpisode | null,
    bgmUrl?: string
  ): Promise<Blob> {
    if (!this.ffmpeg || !this.isLoaded) {
      await this.load();
    }

    if (!this.ffmpeg) {
      throw new Error('FFmpeg 未初始化');
    }

    const zip = new JSZip();

    try {
      // 1. 下载所有资源
      await this.downloadResources(videoClips, bgmUrl);

      // 2. 处理视频素材（裁剪）
      const processedVideos = await this.processVideoClips(videoClips, editConfig);

      // 3. 合并音频素材
      const mergedAudio = await this.mergeAudioClips(videoClips, editConfig);

      // 4. 生成 SRT 字幕文件
      const srtContent = this.generateSubtitles(videoClips, editConfig);

      // 5. 读取处理后的文件并添加到ZIP
      await this.packageFiles(zip, processedVideos, mergedAudio, srtContent, bgmUrl);

      // 5. 生成ZIP文件
      this.updateProgress({
        stage: 'packaging',
        progress: 95,
        message: '正在打包文件...'
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      this.updateProgress({
        stage: 'complete',
        progress: 100,
        message: '分镜素材导出完成！'
      });

      // 清理文件
      await this.cleanup();

      return zipBlob;
    } catch (error) {
      console.error('Storyboard export failed:', error);
      throw error;
    }
  }

  private async downloadResources(videoClips: IVideoClip[], bgmUrl?: string): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    this.updateProgress({
      stage: 'downloading',
      progress: 0,
      message: '正在下载资源...'
    });

    let totalResources = 0;
    let downloaded = 0;

    // 计算总资源数
    for (const clip of videoClips) {
      if (clip.videoUrl) totalResources++;
      if (clip.scriptAudioUrl) totalResources++;
      if (clip.startImage) totalResources++;
      if (clip.endImage) totalResources++;
    }
    if (bgmUrl) totalResources++;

    // 下载视频和音频
    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      
      if (clip.videoUrl) {
        try {
          const videoData = await fetchFile(clip.videoUrl);
          if (videoData && videoData.byteLength > 0) {
            await this.ffmpeg.writeFile(`clip_${i}_video.mp4`, videoData);
            console.log(`[Video] 下载成功: clip_${i}_video.mp4, 大小: ${videoData.byteLength} bytes`);
          }
        } catch (error) {
          console.warn(`[Video] 下载失败: ${clip.videoUrl}`, error);
        }
        downloaded++;
        this.updateProgress({
          stage: 'downloading',
          progress: (downloaded / totalResources) * 100,
          message: `正在下载资源 (${downloaded}/${totalResources})...`
        });
      }
      
      if (clip.scriptAudioUrl) {
        try {
          const audioData = await fetchFile(clip.scriptAudioUrl);
          if (audioData && audioData.byteLength > 0) {
            await this.ffmpeg.writeFile(`clip_${i}_audio.mp3`, audioData);
            console.log(`[Audio] 下载成功: clip_${i}_audio.mp3, 大小: ${audioData.byteLength} bytes`);
          }
        } catch (error) {
          console.warn(`[Audio] 下载失败: ${clip.scriptAudioUrl}`, error);
        }
        downloaded++;
        this.updateProgress({
          stage: 'downloading',
          progress: (downloaded / totalResources) * 100,
          message: `正在下载资源 (${downloaded}/${totalResources})...`
        });
      }
      
      if (clip.startImage) {
        try {
          const imageData = await fetchFile(clip.startImage);
          if (imageData && imageData.byteLength > 0) {
            await this.ffmpeg.writeFile(`clip_${i}_start.jpg`, imageData);
            console.log(`[Image] 下载成功: clip_${i}_start.jpg, 大小: ${imageData.byteLength} bytes`);
          }
        } catch (error) {
          console.warn(`[Image] 下载失败: ${clip.startImage}`, error);
        }
        downloaded++;
        this.updateProgress({
          stage: 'downloading',
          progress: (downloaded / totalResources) * 100,
          message: `正在下载资源 (${downloaded}/${totalResources})...`
        });
      }
      
      if (clip.endImage) {
        try {
          const imageData = await fetchFile(clip.endImage);
          if (imageData && imageData.byteLength > 0) {
            await this.ffmpeg.writeFile(`clip_${i}_end.jpg`, imageData);
            console.log(`[Image] 下载成功: clip_${i}_end.jpg, 大小: ${imageData.byteLength} bytes`);
          }
        } catch (error) {
          console.warn(`[Image] 下载失败: ${clip.endImage}`, error);
        }
        downloaded++;
        this.updateProgress({
          stage: 'downloading',
          progress: (downloaded / totalResources) * 100,
          message: `正在下载资源 (${downloaded}/${totalResources})...`
        });
      }
    }

    // 下载背景音乐
    if (bgmUrl) {
      try {
        const bgmData = await fetchFile(bgmUrl);
        if (bgmData && bgmData.byteLength > 0) {
          await this.ffmpeg.writeFile('bgm.mp3', bgmData);
          console.log(`[BGM] 下载成功: bgm.mp3, 大小: ${bgmData.byteLength} bytes`);
        }
      } catch (error) {
        console.warn(`[BGM] 下载失败: ${bgmUrl}`, error);
      }
      downloaded++;
      this.updateProgress({
        stage: 'downloading',
        progress: 100,
        message: '资源下载完成'
      });
    }
  }

  private async processVideoClips(
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig
  ): Promise<string[]> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const processedVideos: string[] = [];

    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      const clipConfig = editConfig.clips.find(c => c.clipId === clip.id);
      if (!clipConfig) continue;

      // 检查视频文件是否存在
      if (clip.videoUrl) {
        try {
          const videoData = await this.ffmpeg.readFile(`clip_${i}_video.mp4`);
          if (videoData && videoData.length > 0) {
            this.updateProgress({
              stage: 'processing',
              progress: (i / videoClips.length) * 70,
              message: `正在处理视频片段 ${i + 1}/${videoClips.length}...`,
              currentClip: i + 1,
              totalClips: videoClips.length
            });

            const outputFile = `video_${i + 1}.mp4`;
            await this.trimVideoClip(i, clipConfig, outputFile);
            processedVideos.push(outputFile);
          }
        } catch (error) {
          console.warn(`[Video] 视频文件不存在或无效: clip_${i}_video.mp4`);
        }
      }
    }

    return processedVideos;
  }

  private async trimVideoClip(
    index: number,
    clipConfig: any,
    outputFile: string
  ): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const inputVideo = `clip_${index}_video.mp4`;
    const args: string[] = [];

    // 时间裁剪
    if (clipConfig.trimStart !== undefined) {
      args.push('-ss', clipConfig.trimStart.toString());
    }

    args.push('-i', inputVideo);

    if (clipConfig.trimStart !== undefined && clipConfig.trimEnd !== undefined) {
      args.push('-t', (clipConfig.trimEnd - clipConfig.trimStart).toString());
    }

    // 使用快速裁剪
    args.push('-c', 'copy');
    args.push('-avoid_negative_ts', '1');
    args.push(outputFile);

    await this.ffmpeg.exec(args);
  }

  private async mergeAudioClips(videoClips: IVideoClip[], editConfig: VideoEditConfig): Promise<string | null> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    this.updateProgress({
      stage: 'processing',
      progress: 80,
      message: '正在合并音频文件...'
    });

    const audioSegments: string[] = [];

    // 为每个片段生成音频段
    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      const clipConfig = editConfig.clips.find(c => c.clipId === clip.id);
      if (!clipConfig) continue;

      const segmentFile = `audio_segment_${i}.wav`;

      if (clip.scriptAudioUrl) {
        // 检查配音音频是否存在
        try {
          const audioData = await this.ffmpeg.readFile(`clip_${i}_audio.mp3`);
          if (audioData && audioData.length > 0) {
            // 转换为 WAV 格式并裁剪到指定时长
            await this.ffmpeg.exec([
              '-i', `clip_${i}_audio.mp3`,
              '-t', clipConfig.duration.toString(),
              '-c:a', 'pcm_s16le',
              segmentFile
            ]);
            audioSegments.push(segmentFile);
            console.log(`[Audio] 处理音频段 ${i}: ${segmentFile}`);
            continue;
          }
        } catch (error) {
          console.warn(`[Audio] 音频文件不存在: clip_${i}_audio.mp3`);
        }
      }

      // 如果没有配音音频，生成与片段时长一致的空白音频
      await this.ffmpeg.exec([
        '-f', 'lavfi',
        '-i', 'anullsrc=channel_layout=stereo:sample_rate=44100',
        '-t', clipConfig.duration.toString(),
        '-c:a', 'pcm_s16le',
        segmentFile
      ]);
      audioSegments.push(segmentFile);
      console.log(`[Audio] 生成空白音频段 ${i}, 时长: ${clipConfig.duration}s`);
    }

    if (audioSegments.length === 0) {
      console.log('[Audio] 没有音频段需要合并');
      return null;
    }

    // 拼接所有音频段
    const concatList = audioSegments.map(file => `file '${file}'`).join('\n');
    await this.ffmpeg.writeFile('audio_concat.txt', concatList);

    await this.ffmpeg.exec([
      '-f', 'concat',
      '-safe', '0',
      '-i', 'audio_concat.txt',
      '-c:a', 'libmp3lame',
      '-b:a', '192k',
      'merged_audio.mp3'
    ]);

    // 验证输出文件
    try {
      const outputData = await this.ffmpeg.readFile('merged_audio.mp3');
      console.log(`[Audio] 合并完成，输出文件大小: ${outputData.length} bytes`);
      return outputData.length > 0 ? 'merged_audio.mp3' : null;
    } catch (error) {
      console.error('[Audio] 合并失败:', error);
      return null;
    }
  }

  private generateSubtitles(videoClips: IVideoClip[], editConfig: VideoEditConfig): string {
    this.updateProgress({
      stage: 'processing',
      progress: 85,
      message: '正在生成字幕文件...'
    });

    return SRTGenerator.generateSRT(videoClips, editConfig);
  }

  private async packageFiles(
    zip: JSZip,
    processedVideos: string[],
    mergedAudio: string | null,
    srtContent: string,
    bgmUrl?: string
  ): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    this.updateProgress({
      stage: 'packaging',
      progress: 85,
      message: '正在打包视频文件...'
    });

    // 添加视频文件
    const videoFolder = zip.folder('videos');
    for (const videoFile of processedVideos) {
      const data = await this.ffmpeg.readFile(videoFile);
      videoFolder?.file(videoFile, data);
    }

    // 添加图片文件
    const imageFolder = zip.folder('images');
    const files = await this.ffmpeg.listDir('/');
    for (const file of files) {
      if (file.name.endsWith('.jpg') && (file.name.includes('_start') || file.name.includes('_end'))) {
        try {
          const imageData = await this.ffmpeg.readFile(file.name);
          if (imageData && imageData.length > 0) {
            imageFolder?.file(file.name, imageData);
            console.log(`[Package] 添加图片: ${file.name}`);
          }
        } catch (error) {
          console.warn(`[Package] 图片文件读取失败: ${file.name}`);
        }
      }
    }

    // 添加合并的音频文件
    if (mergedAudio) {
      this.updateProgress({
        stage: 'packaging',
        progress: 90,
        message: '正在打包音频文件...'
      });

      const audioFolder = zip.folder('audio');
      const audioData = await this.ffmpeg.readFile(mergedAudio);
      audioFolder?.file('merged_dialogue.mp3', audioData);
    }

    // 添加背景音乐
    if (bgmUrl) {
      const audioFolder = zip.folder('audio');
      try {
        const bgmData = await this.ffmpeg.readFile('bgm.mp3');
        if (bgmData && bgmData.length > 0) {
          audioFolder?.file('background_music.mp3', bgmData);
        }
      } catch (error) {
        console.warn('[Package] 背景音乐文件读取失败');
      }
    }

    // 添加字幕文件
    if (srtContent) {
      zip.file('subtitles.srt', srtContent);
    }

    // 添加说明文件
    const readme = `# 分镜素材包

## 文件说明

### videos/ 目录
包含所有视频片段，已按照时间轴设置进行裁剪：
${processedVideos.map((file, i) => `- ${file}: 视频片段 ${i + 1}`).join('\n')}

### images/ 目录
包含所有开始帧和结束帧图片素材

### audio/ 目录
${mergedAudio ? '- merged_dialogue.mp3: 合并后的对话音频\n' : ''}${bgmUrl ? '- background_music.mp3: 背景音乐\n' : ''}

### 字幕文件
- subtitles.srt: SRT 格式字幕文件，包含所有对话文本和时间轴

## 使用说明
1. videos/ 目录中的视频文件可直接用于视频编辑软件
2. images/ 目录中的图片可作为静态素材使用
3. audio/ 目录中的音频文件可作为配音素材使用
4. subtitles.srt 可导入视频编辑软件作为字幕轨道
5. 所有文件均已按照项目设置进行预处理

导出时间: ${new Date().toLocaleString()}
`;

    zip.file('README.md', readme);
  }

  private async cleanup(): Promise<void> {
    if (!this.ffmpeg) return;

    try {
      const files = await this.ffmpeg.listDir('/');
      for (const file of files) {
        if (file.name.endsWith('.mp4') || 
            file.name.endsWith('.mp3') || 
            file.name.endsWith('.wav') || 
            file.name.endsWith('.txt') ||
            file.name.endsWith('.jpg')) {
          try {
            await this.ffmpeg.deleteFile(file.name);
          } catch (e) {
            // 忽略删除错误
          }
        }
      }
    } catch (error) {
      console.warn('Cleanup failed:', error);
    }
  }

  private updateProgress(progress: StoryboardExportProgress): void {
    if (this.onProgress) {
      this.onProgress(progress);
    }
  }

  async terminate(): Promise<void> {
    if (this.ffmpeg) {
      await this.ffmpeg.terminate();
      this.ffmpeg = null;
      this.isLoaded = false;
    }
  }
}